/**
 * La fiche d'un exercice, ouverte **depuis la séance en direct**
 * *(29/08/2026, après usage)*.
 *
 * On touche le nom ou la vignette, on voit les deux vues du mouvement et son
 * exécution. C'est la question la plus banale qu'on se pose en salle — « je
 * fais quoi, là, exactement ? » — et jusqu'ici il fallait quitter la séance
 * pour aller la chercher dans la bibliothèque.
 *
 * 🔴 **Une `Sheet`, donc par PORTAIL.** Elle s'ouvre depuis une carte de séance,
 * c'est-à-dire depuis un conteneur défilant : `position: fixed` ne se résout
 * pas contre la fenêtre sous un ancêtre portant `transform`, `filter` ou
 * `will-change`. Le projet a déjà payé ce défaut une fois (§6.3 de la V1, une
 * feuille qui s'affichait hors écran) — le portail est ce qui l'empêche de
 * revenir.
 *
 * 🔴 **Le résumé vient de la RÉPLIQUE, pas d'un aller-retour** (`EXERCISES_KEY`,
 * §3.1). Ouvrir la fiche en salle ne coûte donc aucune requête pour tout ce que
 * la liste sait déjà — nom, muscles, matériel, vignettes. Seules les
 * instructions se chargent à la consultation, et restent ensuite disponibles
 * hors ligne (leur clé est persistée).
 *
 * ⚠️ **Un exercice supprimé n'ouvre rien.** `actual_set.exercise_id` est en
 * `SET NULL` (§4.2) : une série d'historique survit à son exercice, mais il n'y
 * a alors plus de fiche à montrer. La séance, elle, continue d'afficher le nom
 * copié — c'est tout l'intérêt de l'avoir copié.
 */

import { useQuery } from '@tanstack/react-query';

import { EXERCISES_KEY, fetchExercises } from '../../api/exercises';
import { Hint, Sheet } from '../../components/ui/primitives';
import { ExerciseFacts } from './ExerciseFacts';

export function ExerciseSheet({
  clientId,
  fallbackName,
  onClose,
}: {
  /** L'exercice à montrer, ou `null` quand la feuille est fermée. */
  clientId: string | null;
  /** Le nom **copié** sur la série, seul survivant d'un exercice supprimé. */
  fallbackName: string;
  onClose: () => void;
}) {
  /*
   * ⚠️ La **même** entrée de cache que la bibliothèque et le sélecteur :
   * ouvrir cette feuille ne déclenche aucun appel supplémentaire, et hors ligne
   * elle lit la réplique restaurée par `lib/persist.ts`.
   */
  const exercises = useQuery({ queryKey: EXERCISES_KEY, queryFn: fetchExercises });
  const exercise = exercises.data?.find((row) => row.clientId === clientId);

  return (
    <Sheet open={clientId !== null} title={exercise?.name ?? fallbackName} onClose={onClose}>
      {exercise === undefined ? (
        <Hint>
          Cet exercice n’est plus dans la base. La séance garde son nom et ses séries — c’est
          l’historique, il ne dépend pas du référentiel.
        </Hint>
      ) : (
        <ExerciseFacts exercise={exercise} />
      )}
    </Sheet>
  );
}
